import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { get, post, put, del } from '../../api';
import CustomersForm from './CustomersForm';
import ImportWizard from './ImportWizard';
import ExportButton from './ExportButton';
import './CustomersTable.css';

const CustomersTable = () => {
  const { user } = useContext(AuthContext);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Pagination and filtering
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize] = useState(20);
  
  // Search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('name:asc');
  
  // UI state
  const [showForm, setShowForm] = useState(false);
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [selectedCustomers, setSelectedCustomers] = useState([]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage === 1) {
        fetchCustomers();
      } else {
        setCurrentPage(1);
      }
    }, 300);

    return () => clearTimeout(timer);
        }, [searchTerm, statusFilter, sortBy]);

  // Fetch customers on page load and page change
  useEffect(() => {
    fetchCustomers();
  }, [currentPage]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: currentPage,
        limit: pageSize,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter }),
        ...(sortBy && { sort: sortBy })
      };

      const response = await get('/contacts/customers', { params });
      const data = response.data;
      
      setCustomers(data.data || []);
      setTotalPages(data.totalPages || 1);
      setTotalItems(data.total || 0);

    } catch (err) {
      console.error('Error fetching customers:', err);
      setError('Failed to load customers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCustomer = () => {
    setEditingCustomer(null);
    setShowForm(true);
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingCustomer(null);
  };

  const handleFormSuccess = (message) => {
    setSuccess(message);
    setShowForm(false);
    setEditingCustomer(null);
    fetchCustomers();
    setTimeout(() => setSuccess(null), 5000);
  };


  const handleImportSuccess = (message) => {
    setSuccess(message);
    setShowImportWizard(false);
    fetchCustomers();
    setTimeout(() => setSuccess(null), 5000);
  };

  const handleExport = async () => {
    try {
      const params = {
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter }),
        ...(sortBy && { sort: sortBy })
      };

      const response = await get('/contacts/customers/export', { 
        params,
        responseType: 'blob'
      });

      // Get filename from response headers
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition 
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `customers_${new Date().toISOString().slice(0, 10)}.csv`;

      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess('Export completed successfully');
      setTimeout(() => setSuccess(null), 5000);

    } catch (err) {
      console.error('Error exporting customers:', err);
      setError('Failed to export customers');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleSelectCustomer = (customerId) => {
    setSelectedCustomers(prev => 
      prev.includes(customerId) 
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCustomers.length === customers.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(customers.map(c => c.customerId));
    }
  };

  const handleDeleteCustomer = async (customer) => {
    if (!window.confirm(`Are you sure you want to delete "${customer.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await del(`/contacts/customers/${customer.customerId}`);
      setSuccess('Customer deleted successfully');
      fetchCustomers();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('Error deleting customer:', err);
      setError('Failed to delete customer');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleMassDelete = async () => {
    if (selectedCustomers.length === 0) {
      setError('No customers selected for deletion');
      setTimeout(() => setError(null), 5000);
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedCustomers.length} selected customer(s)? This action cannot be undone.`)) {
      return;
    }

    try {
      await del('/contacts/customers/mass-delete', {
        data: { customerIds: selectedCustomers }
      });
      
      setSuccess(`${selectedCustomers.length} customers deleted successfully`);
      setSelectedCustomers([]);
      fetchCustomers();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('Error mass deleting customers:', err);
      setError('Failed to delete customers');
      setTimeout(() => setError(null), 5000);
    }
  };

  const canEdit = ['super', 'admin'].includes(user?.role);

  if (loading && customers.length === 0) {
    return (
      <div className="contacts-loading">
        <div>Loading customers...</div>
      </div>
    );
  }

  return (
    <div className="customers-table">
      {error && <div className="contacts-error">{error}</div>}
      {success && <div className="contacts-success">{success}</div>}

      {/* Header with actions */}
      <div className="table-header">
        <div className="header-left">
          <h2>Customers ({totalItems})</h2>
        </div>
        <div className="header-actions">
          {canEdit && (
            <>
              {selectedCustomers.length > 0 && (
                <button 
                  className="btn btn-danger"
                  onClick={handleMassDelete}
                >
                  Delete Selected ({selectedCustomers.length})
                </button>
              )}
              <button 
                className="btn btn-secondary"
                onClick={() => setShowImportWizard(true)}
              >
                Import CSV/XLSX
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleCreateCustomer}
              >
                Add Customer
              </button>
            </>
          )}
          <ExportButton onExport={handleExport} />
        </div>
      </div>

      {/* Filters and search */}
      <div className="table-filters">
        <div className="filter-group">
          <input
            type="text"
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="filter-select"
          >
            <option value="name:asc">Name A-Z</option>
            <option value="name:desc">Name Z-A</option>
            <option value="updatedAt:desc">Recently Updated</option>
            <option value="updatedAt:asc">Oldest Updated</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              {canEdit && (
                <th className="checkbox-col">
                  <input
                    type="checkbox"
                    checked={selectedCustomers.length === customers.length && customers.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
              )}
            <th>Name</th>
            <th>Owner</th>
            <th>User</th>
            <th>Buyer</th>
            <th>Primary Contact</th>
              <th>Status</th>
              <th>Updated</th>
              <th className="actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {customers.length === 0 ? (
              <tr>
                <td colSpan={canEdit ? 9 : 8} className="empty-state">
                  <div className="contacts-empty">
                    <h3>No customers found</h3>
                    <p>Get started by adding your first customer or importing from a CSV file.</p>
                    {canEdit && (
                      <div className="empty-actions">
                        <button 
                          className="btn btn-primary"
                          onClick={handleCreateCustomer}
                        >
                          Add Customer
                        </button>
                        <button 
                          className="btn btn-secondary"
                          onClick={() => setShowImportWizard(true)}
                        >
                          Import CSV
                        </button>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              customers.map((customer) => (
                <tr key={customer.customerId} className={customer.status === 'archived' ? 'archived' : ''}>
                  {canEdit && (
                    <td className="checkbox-col">
                      <input
                        type="checkbox"
                        checked={selectedCustomers.includes(customer.customerId)}
                        onChange={() => handleSelectCustomer(customer.customerId)}
                      />
                    </td>
                  )}
                  <td className="name-cell">
                    <div className="customer-name">{customer.name}</div>
                  </td>
                  <td>
                    <div className="role-contact">
                      <div className="contact-name">{customer.roles?.owner?.name || '-'}</div>
                      {customer.roles?.owner?.phone?.raw && (
                        <div className="contact-phone">
                          <a href={`tel:${customer.roles.owner.phone.e164 || customer.roles.owner.phone.raw}`} className="phone-link">
                            {customer.roles.owner.phone.raw}
                          </a>
                        </div>
                      )}
                      {customer.roles?.owner?.email && (
                        <div className="contact-email">
                          <a href={`mailto:${customer.roles.owner.email}`} className="email-link">
                            {customer.roles.owner.email}
                          </a>
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="role-contact">
                      <div className="contact-name">{customer.roles?.user?.name || '-'}</div>
                      {customer.roles?.user?.phone?.raw && (
                        <div className="contact-phone">
                          <a href={`tel:${customer.roles.user.phone.e164 || customer.roles.user.phone.raw}`} className="phone-link">
                            {customer.roles.user.phone.raw}
                          </a>
                        </div>
                      )}
                      {customer.roles?.user?.email && (
                        <div className="contact-email">
                          <a href={`mailto:${customer.roles.user.email}`} className="email-link">
                            {customer.roles.user.email}
                          </a>
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="role-contact">
                      <div className="contact-name">{customer.roles?.buyer?.name || '-'}</div>
                      {customer.roles?.buyer?.phone?.raw && (
                        <div className="contact-phone">
                          <a href={`tel:${customer.roles.buyer.phone.e164 || customer.roles.buyer.phone.raw}`} className="phone-link">
                            {customer.roles.buyer.phone.raw}
                          </a>
                        </div>
                      )}
                      {customer.roles?.buyer?.email && (
                        <div className="contact-email">
                          <a href={`mailto:${customer.roles.buyer.email}`} className="email-link">
                            {customer.roles.buyer.email}
                          </a>
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="primary-contact">
                      <div className="contact-name">{customer.primaryContact?.name || '-'}</div>
                      {customer.primaryContact?.phone?.raw && (
                        <div className="contact-phone">
                          <a href={`tel:${customer.primaryContact.phone.e164 || customer.primaryContact.phone.raw}`} className="phone-link">
                            {customer.primaryContact.phone.raw}
                          </a>
                        </div>
                      )}
                      {customer.primaryContact?.email && (
                        <div className="contact-email">
                          <a href={`mailto:${customer.primaryContact.email}`} className="email-link">
                            {customer.primaryContact.email}
                          </a>
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${customer.status}`}>
                      {customer.status}
                    </span>
                  </td>
                  <td>{new Date(customer.updatedAt).toLocaleDateString()}</td>
                  <td className="actions-col">
                    <div className="action-buttons">
                      <button 
                        className="edit-btn"
                        onClick={() => handleEditCustomer(customer)}
                      >
                        Edit
                      </button>
                      {canEdit && (
                        <button 
                          className="delete-btn"
                          onClick={() => handleDeleteCustomer(customer)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}

      {/* Modals */}
      {showForm && (
        <CustomersForm
          customer={editingCustomer}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}

      {showImportWizard && (
        <ImportWizard
          type="customers"
          onClose={() => setShowImportWizard(false)}
          onSuccess={handleImportSuccess}
        />
      )}
    </div>
  );
};

export default CustomersTable;
